import React, { useState, useEffect, useCallback } from 'react';
import { Table, Badge, Input, Button } from 'antd';
import { SyncOutlined, SearchOutlined, CheckCircleOutlined, ClockCircleOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { fetchAllInboxMessages } from '../services';
import type { InboxMonitorMessage } from '../services';

const InboxMonitorPanel: React.FC = () => {
  const [data, setData] = useState<InboxMonitorMessage[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [expandedKeys, setExpandedKeys] = useState<string[]>([]);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchAllInboxMessages(300);
      setData(res);
    } catch (err) {
      console.error('Fetch inbox messages error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const filtered = search
    ? data.filter(m =>
        m.fromUserName?.includes(search) ||
        m.toUserName?.includes(search) ||
        m.subject?.includes(search) ||
        m.content?.includes(search)
      )
    : data;

  const fmtTime = (v: string | { _seconds?: number; seconds?: number }) => {
    if (!v) return '--';
    if (typeof v === 'object') {
      const secs = v._seconds ?? v.seconds;
      if (secs) return new Date(secs * 1000).toLocaleString('zh-TW');
      return '--';
    }
    return new Date(v).toLocaleString('zh-TW');
  };

  const columns: ColumnsType<InboxMonitorMessage> = [
    {
      title: '時間', dataIndex: 'createdAt', width: 160,
      render: (v: string) => <span className="text-xs">{fmtTime(v)}</span>,
      sorter: (a, b) => {
        const ta = typeof a.createdAt === 'object' ? ((a.createdAt as unknown as Record<string, number>)._seconds || 0) : new Date(a.createdAt).getTime() / 1000;
        const tb = typeof b.createdAt === 'object' ? ((b.createdAt as unknown as Record<string, number>)._seconds || 0) : new Date(b.createdAt).getTime() / 1000;
        return ta - tb;
      },
      defaultSortOrder: 'descend',
    },
    {
      title: '寄件人', dataIndex: 'fromUserName', width: 120,
      render: (v: string, r: InboxMonitorMessage) => (
        <span className="text-xs font-medium">{v || r.fromUserId}</span>
      ),
    },
    {
      title: '收件人', dataIndex: 'toUserName', width: 120,
      render: (v: string, r: InboxMonitorMessage) => (
        <span className="text-xs font-medium">{v || r.toUserId}</span>
      ),
    },
    {
      title: '主旨', dataIndex: 'subject', ellipsis: true,
      render: (v: string) => <span className="text-xs">{v}</span>,
    },
    {
      title: '回覆', dataIndex: 'replies', width: 70, align: 'center',
      render: (replies: InboxMonitorMessage['replies']) => {
        const count = replies?.length || 0;
        return count > 0
          ? <Badge count={count} style={{ backgroundColor: '#1677ff' }} />
          : <span className="text-gray-300">0</span>;
      },
    },
    {
      title: '已讀', dataIndex: 'isRead', width: 60, align: 'center',
      render: (v: boolean) => v
        ? <CheckCircleOutlined className="text-green-500" />
        : <ClockCircleOutlined className="text-orange-400" />,
    },
  ];

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-base font-semibold text-gray-700">收件夾對話紀錄</h3>
        <div className="flex gap-2">
          <Input
            placeholder="搜尋寄件人、收件人、主旨..."
            prefix={<SearchOutlined />}
            size="small"
            style={{ width: 260 }}
            value={search}
            onChange={e => setSearch(e.target.value)}
            allowClear
          />
          <Button icon={<SyncOutlined spin={loading} />} size="small" onClick={reload}>刷新</Button>
        </div>
      </div>

      <Table
        dataSource={filtered}
        columns={columns}
        rowKey="id"
        size="small"
        loading={loading}
        pagination={{ pageSize: 20, showSizeChanger: true, showTotal: (t: number) => `共 ${t} 封` }}
        expandable={{
          expandedRowKeys: expandedKeys,
          onExpand: (expanded, record) => {
            setExpandedKeys(expanded ? [...expandedKeys, record.id] : expandedKeys.filter(k => k !== record.id));
          },
          expandedRowRender: (record) => (
            <div className="pl-4 py-2 space-y-3">
              {/* Original message */}
              <div className="bg-blue-50 rounded-lg p-3">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs font-bold text-blue-700">{record.fromUserName || record.fromUserId}</span>
                  <span className="text-xs text-gray-400">{'\u2192'} {record.toUserName || record.toUserId}</span>
                  <span className="text-xs text-gray-400 ml-auto">{fmtTime(record.createdAt)}</span>
                </div>
                <div className="text-sm whitespace-pre-wrap text-gray-800">{record.content || '(無內容)'}</div>
              </div>

              {/* Replies */}
              {record.replies?.map((reply, i) => (
                <div key={i} className="bg-gray-50 rounded-lg p-3 ml-6 border-l-2 border-blue-200">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs font-bold text-gray-700">{reply.fromUserName || reply.fromUserId}</span>
                    <span className="text-xs text-gray-400 ml-auto">{fmtTime(reply.createdAt)}</span>
                  </div>
                  <div className="text-sm whitespace-pre-wrap text-gray-700">{reply.content}</div>
                </div>
              ))}

              {(!record.replies || record.replies.length === 0) && (
                <div className="text-xs text-gray-400 ml-6">尚無回覆</div>
              )}
            </div>
          ),
        }}
      />
    </div>
  );
};

export default InboxMonitorPanel;
